import React from 'react';
import { Input, Button, Select, PaperClip, Checkbox } from 'components';
import { connect } from 'react-redux';
import { AuthService } from 'services';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router';
import { BackIcon, PasswordActive, PasswordInactive } from 'assets/icons';
import { Link } from 'react-router-dom';
import { UserImage } from 'assets/img';
import { Constants, Globals, Print } from 'utils';
import COUNTRIES from 'utils/countries';
import PHONE_CODES from 'utils/phonecodes';
import { isValidPhoneNumber } from 'libphonenumber-js';

declare var window: any;

interface MatchParams {
  level_id: string;
}

interface Props extends RootState, RouteComponentProps<MatchParams> {
  dispatch: any;
}

const STEPS = {
  DATA: 1,
  VALIDATION: 2,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class Register extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    for (let i = 0; i < 6; i++) {
      // @ts-ignore
      this['ref' + i] = React.createRef();
    }
  }

  state = {
    step: STEPS.DATA,
    code: [],
    activePassword: false,
    activePasswordConfirmation: false,
    send_by: 'sms' as 'sms' | 'email',
    sms_failed: false,
    form: {
      photo: '',
      name: '',
      lastname: '',
      phone: '',
      phone_code: PHONE_CODES[0].value,
      document: '',
      document_type_id: '',
      email: '',
      password: '',
      password_confirmation: '',
      city_id: '',
      state_id: '',
      country: '',
      is_checked: false,
    },
    document_types: [],
    level_id: parseInt(this.props.match.params.level_id),
    states: [],
    cities: [],
  };

  componentDidMount() {
    if ([Constants.LEVELS.CLIENT, Constants.LEVELS.LAWYER].indexOf(this.state.level_id) == -1) {
      this.props.history.replace('/login');
    }

    let code: any = [...this.state.code];
    for (let i = 0; i < 6; i++) {
      code.push('');
    }
    this.setState({ code });

    this.load();
  }

  load = async () => {
    const res: any = await AuthService.getDocumentTypes();
    this.setState({
      document_types: res.document_types || [],
      form: {
        ...this.state.form,
        document_type_id: res.document_types.length > 0 ? res.document_types[0].id : '',
      },
      states: res.states || [],
    });
  };

  getErrorMessage = (error: any): string => {
    const err = error?.response?.data?.error;

    if (!err) {
      return 'Ocurrió un error inesperado. Por favor, intente más tarde.';
    }
    if (typeof err === 'string') {
      return err;
    }
    if (typeof err === 'object' && err !== null) {
      return (
        err.message ??
        err.error ??
        'Error de validación en el servidor. Por favor, intente más tarde.'
      );
    }
    return 'Error de validación en el servidor. Por favor, intente más tarde.';
  };

  submit = async (e: any = null) => {
    if (e) e.preventDefault();

    if (this.state.code.filter((i) => !i).length > 0) {
      Globals.showError('Introduzca todos los números del código de confirmación');
      return;
    }

    try {
      const res: any = await AuthService.register({
        hasFile: true,
        ...this.state.form,
        level_id: this.state.level_id,
        code: this.state.code.join(''),
        // @ts-ignore
        photo: this.state.form.photo?.value,
      });

      if (res.user) {
        await this.props.dispatch({
          type: 'SET_USER',
          payload: res.user,
        });
        // El backend ya emite access_token + refresh_token al registrarse
        // (igual que en login) -- sin este dispatch, el store se quedaba con
        // los tokens en null y la primera llamada autenticada salía sin
        // Authorization header, cayendo en un 401 -> intento de refresh sin
        // refresh_token guardado -> forceLogout inmediato tras verificar el código.
        await this.props.dispatch({
          type: 'SET_AUTH_TOKENS',
          payload: {
            access_token: res.access_token,
            refresh_token: res.refresh_token,
          },
        });
        // Redirigir agregando la variable por URL para mostrar el modal de bienvenida
        this.props.history.replace({ pathname: '/', search: '?welcome=true' });
        // this.props.history.replace('/');
      }
    } catch (error: any) {
      console.error('Error en el registro:', error);
      Globals.showError(this.getErrorMessage(error));
    }
  };

  changeCode = async (e: any, index: number) => {
    let code: any = [...this.state.code];
    code[index] = e.target.value;
    await this.setState({ code });
    if (e != '') {
      // @ts-ignore
      if (this['ref' + (index + 1)]) {
        // @ts-ignore
        this['ref' + (index + 1)].current.focus();
      }
    }
  };

  setStep = (step: number) => {
    this.setState({ step });
  };

  back = (e: any) => {
    e.preventDefault();
    const { step } = this.state;
    if (step == STEPS.DATA) {
      this.props.history.goBack();
    } else {
      this.setStep(STEPS.DATA);
    }
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  changeCountry = (e: any) => {
    this.setState({
      form: {
        ...this.state.form,
        country: e.target.value,
        state_id: '',
        city_id: '',
      },
      cities: [],
    });
  };

  getCities = async () => {
    const res: any = await AuthService.getCities({
      state_id: this.state.form.state_id,
    });
    this.setState({ cities: res.cities || [] });
  };

  validateRegister = async () => {
    const { form } = this.state;
    const isVenezuela = form.country === 'VE';

    // Solo letras (incluyendo acentos y ñ) y espacios
    const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;

    // Campos obligatorios para todos
    if (!form.name.trim()) {
      Globals.showError('Por favor, ingrese su nombre');
      return;
    }
    if (!nameRegex.test(form.name.trim())) {
      Globals.showError('El nombre no debe contener números ni signos de puntuación');
      return;
    }

    if (!form.lastname.trim()) {
      Globals.showError('Por favor, ingrese su apellido');
      return;
    }
    if (!nameRegex.test(form.lastname.trim())) {
      Globals.showError('El apellido no debe contener números ni signos de puntuación');
      return;
    }

    if (!form.document.trim()) {
      Globals.showError('Por favor, ingrese su número de documento');
      return;
    }
    if (form.document.trim().length < 5) {
      Globals.showError('El número de documento debe tener al menos 6 dígitos');
      return;
    }
    if (!form.phone.trim()) {
      Globals.showError('Por favor, ingrese su número de teléfono');
      return;
    }

    const isPhoneValid = isValidPhoneNumber(form.phone, form.phone_code);
    if (!isPhoneValid) {
      Globals.showError('Por favor, ingrese un número de teléfono válido para su país');
      return;
    }
    if (!form.email.trim()) {
      Globals.showError('Por favor, ingrese su correo electrónico');
      return;
    }
    if (!EMAIL_REGEX.test(form.email.trim())) {
      Globals.showError('Por favor, ingrese un correo electrónico válido');
      return;
    }
    if (!form.country) {
      Globals.showError('Por favor, seleccione su país');
      return;
    }

    // Estado y ciudad: obligatorios solo si es Venezuela
    if (isVenezuela && !form.state_id) {
      Globals.showError('Por favor, seleccione su estado');
      return;
    }
    if (isVenezuela && !form.city_id) {
      Globals.showError('Por favor, seleccione su ciudad');
      return;
    }

    if (!form.password) {
      Globals.showError('Por favor, ingrese una contraseña');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;
    if (!passwordRegex.test(form.password)) {
      Globals.showError(
        'La contraseña debe tener al menos una mayúscula, una minúscula, un número y un símbolo',
      );
      return;
    }

    if (!form.password_confirmation) {
      Globals.showError('Por favor, confirme su contraseña');
      return;
    }
    if (form.password !== form.password_confirmation) {
      Globals.showError('Las contraseñas no coinciden');
      return;
    }

    if (!form.is_checked) {
      Globals.showError('Por favor, lea los términos y condiciones de uso de esta app');
      return;
    }

    try {
      const initialSendBy = form.phone_code === 'VE' ? 'sms' : 'email';
      const payload = {
        ...form,
        photo: null,
        level_id: this.state.level_id,
        send_by: initialSendBy as 'sms' | 'email',
      };
      console.log('Enviando datos a la API:', payload);

      // El backend retorna { send_by: 'sms' | 'email' } con el canal que realmente se usó.
      // Si SMS falló y hubo fallback a email, el backend lo indica aquí
      // y el frontend renderiza la pantalla correcta sin suposiciones.
      const res: any = await AuthService.validateRegister(payload);
      const actualSendBy: 'sms' | 'email' = res?.send_by ?? initialSendBy;

      if (initialSendBy === 'sms' && actualSendBy === 'email') {
        console.warn(
          '[Verificacion] Se solicitó envío por SMS pero el servidor hizo fallback a email. Revise el servicio de MassivaMovil.',
        );
        // Marcamos sms_failed para ocultar el botón de reenviar por SMS en la pantalla de email.
        this.setState({
          step: STEPS.VALIDATION,
          send_by: actualSendBy,
          sms_failed: true,
        });
      } else {
        this.setState({
          step: STEPS.VALIDATION,
          send_by: actualSendBy,
          sms_failed: false,
        });
      }
    } catch (error: any) {
      console.error('Error en la petición:', error);
      Globals.showError(this.getErrorMessage(error));
    }
  };

  resendBySms = async () => {
    const { form } = this.state;
    try {
      const payload = {
        ...form,
        photo: null,
        level_id: this.state.level_id,
        send_by: 'sms' as const,
      };
      await AuthService.validateRegister(payload);
      this.setState({ send_by: 'sms' });
      Globals.showSuccess('Código enviado a tu número de teléfono vía SMS');
    } catch (error: any) {
      console.error('Error al reenviar por SMS:', error);
      Globals.showError(this.getErrorMessage(error));
    }
  };

  resendByEmail = async () => {
    const { form } = this.state;
    try {
      const payload = {
        ...form,
        photo: null,
        level_id: this.state.level_id,
        send_by: 'email' as const,
      };
      await AuthService.validateRegister(payload);
      this.setState({ send_by: 'email' });
      Globals.showSuccess('Código enviado a tu correo electrónico');
    } catch (error: any) {
      console.error('Error al reenviar por correo:', error);
      Globals.showError(this.getErrorMessage(error));
    }
  };

  handlePaste = async (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const digits = pasted.replace(/\D/g, '').slice(0, 6);
    if (digits.length === 6) {
      const code: any = digits.split('');
      await this.setState({ code });
      // Foco al último campo
      // @ts-ignore
      this['ref5']?.current?.focus();
    } else if (digits.length === 1) {
      // Si pega un solo dígito, comportamiento normal
      this.changeCode({ target: { value: digits } }, index);
    }
  };

  openFile = async () => {
    Globals.setLoading();
    if (window.webkit && window.webkit.messageHandlers) {
      const message = 'open_url';
      const sendToNative = {
        message,
        url: Constants.BASE_PUBLIC + 'terms/terms.pdf',
      };
      window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(sendToNative));
    } else {
      Print(Constants.BASE_PUBLIC + 'terms/terms.pdf');
    }
    Globals.quitLoading();
  };

  render() {
    const { step, form } = this.state;
    const isVenezuela = form.country === 'VE';
    const isVenezuelanPhone = form.phone_code === 'VE';
    const phoneEntry = PHONE_CODES.find((p) => p.value === form.phone_code);
    const callingCode = phoneEntry?.callingCode ?? form.phone_code;
    const { sms_failed } = this.state;

    return (
      <div id='register'>
        <Link to='/login' className='back' onClick={(e: any) => this.back(e)}>
          <img src={BackIcon} />
        </Link>
        <form onSubmit={this.submit}>
          <h4>Registro</h4>
          <div className='container-steps'>
            <div className={`item-step ${step >= STEPS.DATA ? 'active' : ''}`}>
              <p>Datos</p>
            </div>
            <div className='separator'></div>
            <div className={`item-step ${step >= STEPS.VALIDATION ? 'active' : ''}`}>
              <p>Verificación</p>
            </div>
          </div>

          {step == STEPS.DATA && (
            <div className='row row-data'>
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={form.name}
                  name='name'
                  placeholder='Nombre'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={form.lastname}
                  name='lastname'
                  placeholder='Apellido'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <div className='container-select'>
                  <Select
                    color='white'
                    placeholder='Seleccionar'
                    name='document_type_id'
                    onChange={this.change}
                    value={form.document_type_id}
                    options={this.state.document_types.map((item: any) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                  />
                  <Input
                    type='number'
                    color='white'
                    value={form.document}
                    name='document'
                    maxLength={9}
                    minLength={5}
                    onChange={this.change}
                  />
                </div>
              </div>
              <div className='col-md-6'>
                <div className='container-select'>
                  <Select
                    color='white'
                    placeholder='Código'
                    name='phone_code'
                    style={{ width: 115 }}
                    onChange={this.change}
                    value={this.state.form.phone_code} // Esto ahora guardará "US", "VE", etc.
                    options={PHONE_CODES}
                  />

                  <Input
                    type='number'
                    color='white'
                    maxLength={15}
                    value={this.state.form.phone}
                    name='phone'
                    onChange={this.change}
                  />
                </div>
              </div>

              {/* Correo y País en la misma fila */}
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={form.email}
                  name='email'
                  placeholder='Correo electrónico'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <Select
                  color='white'
                  placeholder='País'
                  name='country'
                  onChange={this.changeCountry}
                  value={form.country}
                  options={COUNTRIES}
                />
              </div>

              {/* Estado */}
              <div className='col-md-6'>
                <Select
                  disabled={!isVenezuela}
                  color='white'
                  placeholder='Estado'
                  name='state_id'
                  className={!isVenezuela ? 'select-disabled' : ''}
                  onChange={(e: any) =>
                    this.change(e, () => {
                      this.getCities();
                    })
                  }
                  value={form.state_id}
                  options={this.state.states.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </div>

              {/* Ciudad */}
              <div className='col-md-6'>
                <Select
                  disabled={!isVenezuela || form.state_id === ''}
                  color='white'
                  placeholder='Ciudad'
                  name='city_id'
                  className={!isVenezuela || form.state_id === '' ? 'select-disabled' : ''}
                  onChange={this.change}
                  value={form.city_id}
                  options={this.state.cities.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </div>

              <div className='col-md-6'>
                <div className='input-password-wrapper'>
                  <Input
                    type={this.state.activePassword ? 'text' : 'password'}
                    color='white'
                    value={form.password}
                    name='password'
                    placeholder='Contraseña'
                    onChange={this.change}
                  />
                  <div
                    className='password-view'
                    onClick={() =>
                      this.setState({
                        activePassword: !this.state.activePassword,
                      })
                    }
                  >
                    <img src={this.state.activePassword ? PasswordActive : PasswordInactive} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='input-password-wrapper'>
                  <Input
                    type={this.state.activePasswordConfirmation ? 'text' : 'password'}
                    color='white'
                    value={form.password_confirmation}
                    name='password_confirmation'
                    placeholder='Repetir contraseña'
                    onChange={this.change}
                  />
                  <div
                    className='password-view'
                    onClick={() =>
                      this.setState({
                        activePasswordConfirmation: !this.state.activePasswordConfirmation,
                      })
                    }
                  >
                    <img
                      src={
                        this.state.activePasswordConfirmation ? PasswordActive : PasswordInactive
                      }
                    />
                  </div>
                </div>
              </div>
              <div className='col-md-12' role='button' onClick={this.openFile}>
                <p className='title text-start'>Términos y condiciones</p>
              </div>
              <div className='col-md-12'>
                <Checkbox
                  label={
                    <span>
                      Declaro que he leído los{' '}
                      <a href='https://legalmenteapp.com/terms' target='_blank' rel='noreferrer'>
                        términos y condiciones
                      </a>
                      , la{' '}
                      <a href='https://legalmenteapp.com/privacy' target='_blank' rel='noreferrer'>
                        política de privacidad
                      </a>{' '}
                      y{' '}
                      <a
                        href='https://legalmenteapp.com/arbitration'
                        target='_blank'
                        rel='noreferrer'
                      >
                        la cláusula de arbitraje
                      </a>{' '}
                      de esta app
                    </span>
                  }
                  checked={form.is_checked}
                  onChange={() => {
                    this.setState({
                      form: { ...form, is_checked: !form.is_checked },
                    });
                  }}
                  name='is_checked'
                />
              </div>
              <div className='col-md-12 text-center'>
                <Button type='button' className='btn-next' onClick={() => this.validateRegister()}>
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {step == STEPS.VALIDATION && (
            <div className='row'>
              <div className='col-md-12'>
                {this.state.send_by === 'sms' ? (
                  <>
                    <div className='message-confirmation-email'>
                      <p>
                        El <b>código de verificación</b> ha sido enviado a tu número de teléfono vía{' '}
                        <b>SMS</b>
                      </p>
                    </div>
                    <p className='subtitle'>
                      ¿No recibiste el SMS a{' '}
                      <b>
                        {callingCode} {form.phone}
                      </b>
                      ? {/* Solo venezolanos ven la opción de cambiar a email */}
                      {isVenezuelanPhone && (
                        <button
                          type='button'
                          className='email-link'
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: '#0400eeff',
                          }}
                          onClick={this.resendByEmail}
                        >
                          Enviar el código por correo electrónico
                        </button>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <div className='message-confirmation-email'>
                      <p>
                        Revise su bandeja de entrada o de <b>spam</b> para obtener el{' '}
                        <b>código de confirmación</b>
                      </p>
                    </div>
                    <p className='subtitle'>
                      El código de confirmación ha sido enviado a{' '}
                      <a
                        className='email-link'
                        href='http://gmail.com'
                        target='_blank'
                        rel='noreferrer'
                      >
                        {form.email}
                      </a>
                    </p>
                    {/* Ocultar botón de SMS si es fallback por error, o si no es venezolano */}
                    {isVenezuelanPhone && !sms_failed && (
                      <p className='subtitle'>
                        ¿No recibiste el correo?{' '}
                        <button
                          type='button'
                          className='email-link'
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: '#0400eeff',
                          }}
                          onClick={this.resendBySms}
                        >
                          Enviar el código por SMS a{' '}
                          <b>
                            {callingCode} {form.phone}
                          </b>
                        </button>
                      </p>
                    )}
                  </>
                )}
                <div className='container-code'>
                  {this.state.code.map((item, index) => (
                    <div className='item-code' key={index}>
                      <Input
                        maxLength={1}
                        value={item}
                        onChange={(e: any) => this.changeCode(e, index)}
                        onPaste={(e: any) => this.handlePaste(e, index)}
                        type='number'
                        // @ts-ignore
                        ref={this['ref' + index]}
                      />
                    </div>
                  ))}
                </div>
                <p className='text-hint'>
                  {this.state.send_by === 'sms'
                    ? 'Por favor, introduzca el código enviado a su teléfono'
                    : 'Por favor, introduzca el código enviado a su correo'}
                </p>
              </div>
              <div className='col-md-12 text-center'>
                <Button type='submit'>Aceptar</Button>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return { user: state.user };
})(Register);
